import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';

import { Stack } from '@mui/material';
import moment from 'moment';

import { KaseApi, KasePropertyApi } from '@/api';
import { DATE_TIME_FORMAT } from '@/config';
import { useKaseContext } from '@/hooks';
import { IKaseCfsLink, IProperty } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import { useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

import { PrintLabelItem } from './PrintLabelItem';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import { propertyPrintLabelFields } from '../../data';
import { useKasePropertyContext } from '../../PropertyContext';

export const PropertyPrintLabel = () => {
  const { PropertyType } = useKaseMainContext();
  const { kase } = useKaseContext();
  const { currentPropertyId } = useKasePropertyContext();
  const { updateToast } = useToastStore();
  const [propertyDetails, setPropertyDetails] = useState<IProperty>();
  const [kaseCfsLinks, setKaseCfsLinks] = useState<IKaseCfsLink[]>([]);

  const printingArea = useRef<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({});

  useEffect(() => {
    const fetchLinkedKaseCfs = async () => {
      if (!kase?._id) return;

      try {
        const res = await KaseApi.getLinkedCFS(kase._id);
        setKaseCfsLinks((res?.data as any)?.results || res?.data);
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    fetchLinkedKaseCfs();
  }, [kase?._id]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!currentPropertyId || !kase?._id) return;
      try {
        const response = await KasePropertyApi.getOne(
          kase?._id,
          currentPropertyId,
        );
        setPropertyDetails(response.data);
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    fetchPropertyDetails();
  }, [currentPropertyId, kase?._id]);

  const getItemDynamically = (key: string) => {
    const personDetails = formatPersonName(propertyDetails?.createdBy);
    const dateLogged = propertyDetails?.loggedInfo?.loggedDate
      ? moment(propertyDetails?.loggedInfo?.loggedDate).format(DATE_TIME_FORMAT)
      : '';

    const allPropertyTypes = PropertyType.values();
    const physicalType =
      propertyDetails?.type && PropertyType.size
        ? [...allPropertyTypes].find(
            (value) => value.key === propertyDetails?.type,
          )?.label
        : undefined;
    const valueLookup: Record<string, string | undefined> = {
      propertyType: propertyDetails?.typeTag || '',
      submittedBy: personDetails || '',
      date: dateLogged,
      description: propertyDetails?.description || '',
      ...(physicalType && { type: physicalType }),
    };

    return valueLookup[key] || '';
  };

  return (
    <Stack>
      <SideModalTitle titleClassName="customVariant">
        Print label
      </SideModalTitle>
      <Stack ref={printingArea} sx={{ p: 2 }}>
        {propertyPrintLabelFields.map(({ key, items }) => (
          <PrintLabelItem
            key={key}
            keyId={key}
            kase={kase || null}
            items={items}
            propertyDetails={
              {
                ...propertyDetails,
                relatedCFSNumber: kaseCfsLinks?.[0]?.cfs?.number || '',
              } as IProperty & { relatedCFSNumber: string }
            }
            getItemValue={getItemDynamically}
          />
        ))}
      </Stack>
      <PropertyModalFooter
        onPrint={() => handlePrint(() => printingArea.current)}
      />
    </Stack>
  );
};
